import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";
import { Badge } from "../ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { FileSearch, Sigma, Table2, GitBranch, ArrowDownToLine, ArrowUpFromLine, ExternalLink, ShieldCheck, ShieldAlert } from "lucide-react";

const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-");

function DepList({ items, icon: Icon, title, dir }) {
  const navigate = useNavigate();
  return (
    <div className="flex-1 min-w-[220px]">
      <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5"><Icon className="h-3 w-3" /> {title}</div>
      <div className="space-y-1.5">
        {items.map((d) => (
          <button key={d.label + d.route} onClick={() => navigate(d.route)} data-testid={`prov-${dir}-${slug(d.label)}`}
            className="w-full text-left border border-border rounded-sm px-2.5 py-1.5 hover:border-accent/50 hover:bg-surface-hover transition-colors group/dep">
            <div className="text-xs font-medium flex items-center justify-between gap-1">
              <span className="truncate">{d.label}</span>
              <ExternalLink className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover/dep:opacity-100 shrink-0" />
            </div>
            {d.desc && <div className="text-[10px] text-muted-foreground truncate">{d.desc}</div>}
          </button>
        ))}
        {!items.length && <div className="text-[11px] text-muted-foreground">None mapped.</div>}
      </div>
    </div>
  );
}

function ProvenancePanel({ pkey, pp, year, onClose }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const params = {};
    if (year) params.year = year;
    if (pp) params.p = pp;
    api.get(`/provenance/${encodeURIComponent(pkey)}`, { params })
      .then((r) => setD(r.data))
      .catch((e) => setErr(apiErr(e)));
  }, [pkey, pp, year]);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="provenance-panel">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <FileSearch className="h-4 w-4 text-accent" /> Provenance Analysis
          </SheetTitle>
        </SheetHeader>
        {err && <div className="text-sm text-red-400 mt-4" data-testid="prov-error">{err}</div>}
        {!d && !err && <div className="text-sm text-muted-foreground mt-4 animate-pulse">Tracing lineage…</div>}
        {d && (
          <div className="mt-3 space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm" data-testid="prov-title">{d.title}</span>
              <Badge variant="outline" className="text-[9px] uppercase">{d.module}</Badge>
              <Badge variant="outline" className="text-[9px] font-mono">FY{d.year}</Badge>
              <span className="text-[10px] text-muted-foreground font-mono">read-only · curated definition</span>
            </div>

            {/* Calculation / formula */}
            <section data-testid="prov-formula">
              <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Sigma className="h-3.5 w-3.5" /> Calculation / formula</div>
              <div className="card-flat p-3 space-y-2">
                <div className="font-mono text-xs leading-relaxed text-accent" data-testid="prov-expression">{d.formula.expression}</div>
                <div className="font-mono text-[11px] text-foreground border-t border-border pt-2" data-testid="prov-substitution">
                  = {d.formula.substitution}
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {d.formula.filters.map((f) => <Badge key={f} variant="outline" className="text-[9px]">{f}</Badge>)}
                </div>
                {d.formula.factor_sources?.length > 0 && (
                  <div className="text-[10px] text-muted-foreground pt-1">
                    <span className="text-foreground/70">Factors & weightings: </span>{d.formula.factor_sources.join(" · ")}
                  </div>
                )}
              </div>
            </section>

            {/* Integrity / cross-check band (F-036) */}
            <section data-testid="prov-integrity">
              <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                {d.integrity && !d.integrity.consistent
                  ? <ShieldAlert className="h-3.5 w-3.5 text-red-400" />
                  : <ShieldCheck className="h-3.5 w-3.5 text-[#00E65B]" />} Integrity cross-check
                {d.integrity && (
                  <Badge variant={d.integrity.consistent ? "success" : "danger"} className="text-[9px]" data-testid="prov-integrity-status">
                    {d.integrity.consistent ? "consistent" : `${d.integrity.divergences} divergence${d.integrity.divergences === 1 ? "" : "s"}`}
                  </Badge>
                )}
              </div>
              {!d.integrity && (
                <div className="text-[11px] text-muted-foreground" data-testid="prov-integrity-none">
                  No cross-checks defined for this widget yet — Control Tower widgets first.
                </div>
              )}
              {d.integrity && (
                <div className="space-y-1.5">
                  {d.integrity.checks.map((c, i) => (
                    <div key={i} data-testid={`prov-integrity-row-${i}`}
                      className={`border rounded-sm px-2.5 py-2 text-xs ${c.consistent ? "border-border" : "border-red-500/60 bg-red-500/5"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{c.quantity}</span>
                        <Badge variant={c.consistent ? "success" : "danger"} className="text-[9px] shrink-0">
                          {c.consistent ? "consistent" : "divergence"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        {[c.a, c.b].map((sfc, j) => (
                          <button key={j} onClick={() => navigate(sfc.route)}
                            className="text-left border border-border rounded-sm px-2 py-1 hover:border-accent/50 transition-colors">
                            <div className="text-[10px] text-muted-foreground truncate">{sfc.surface}</div>
                            <div className={`font-mono text-xs ${c.consistent ? "" : "text-red-400 font-bold"}`}>
                              {sfc.value != null ? Number(sfc.value).toLocaleString() : "—"}
                            </div>
                          </button>
                        ))}
                      </div>
                      {c.note && <div className="text-[10px] text-muted-foreground mt-1">{c.note}</div>}
                    </div>
                  ))}
                  <div className="text-[10px] text-muted-foreground">{d.integrity.coverage_note}</div>
                </div>
              )}
            </section>

            {/* Data sources */}
            <section data-testid="prov-sources">
              <div className="label-eyebrow mb-2 flex items-center gap-1.5">
                <Table2 className="h-3.5 w-3.5" /> Data sources
                <span className="text-[10px] text-muted-foreground font-mono normal-case tracking-normal" data-testid="prov-source-count">
                  showing {d.sources.shown} of {d.sources.total}
                </span>
              </div>
              <div className="card-flat divide-y divide-border max-h-[300px] overflow-y-auto">
                {d.sources.rows.map((r, i) => (
                  <button key={i} onClick={() => navigate(r.route)} data-testid="prov-source-row"
                    className="w-full text-left px-3 py-2 hover:bg-surface-hover transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate">{r.label}</span>
                      <span className="font-mono text-xs shrink-0">
                        {r.value != null ? Number(r.value).toLocaleString() : "—"}{r.unit ? ` ${r.unit}` : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <span className="text-[10px] text-muted-foreground font-mono truncate">
                        {r.period || "—"} · {r.facility} · {r.channel}{r.co2e != null ? ` · ${r.co2e} tCO₂e` : ""}
                      </span>
                      <span className="text-[9px] text-muted-foreground/60 font-mono shrink-0">{r.by}{r.at ? ` · ${r.at}` : ""}</span>
                    </div>
                  </button>
                ))}
                {!d.sources.rows.length && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No contributing records for this period.</div>}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5">Click a row to open the source module (per-record lineage lives there).</div>
            </section>

            {/* Dependency links */}
            <section data-testid="prov-deps">
              <div className="label-eyebrow mb-2 flex items-center gap-1.5"><GitBranch className="h-3.5 w-3.5" /> Dependency links
                <button onClick={() => navigate("/data-flow")} data-testid="prov-open-dataflow"
                  className="ml-auto text-[10px] font-mono normal-case tracking-normal text-accent hover:underline inline-flex items-center gap-1">
                  full data-flow map <ExternalLink className="h-2.5 w-2.5" />
                </button>
              </div>
              <div className="flex gap-4 flex-wrap">
                <DepList items={d.upstream} icon={ArrowDownToLine} title="Fed by (upstream)" dir="up" />
                <DepList items={d.downstream} icon={ArrowUpFromLine} title="Consumed by (downstream)" dir="down" />
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function ProvenanceTrigger({ pkey, pp, year, className }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const always = user?.role === "auditor";
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        title="Provenance analysis — sources, formula & dependencies"
        data-testid={`prov-trigger-${slug(pkey)}${pp ? `-${slug(pp)}` : ""}`}
        className={`${always ? "opacity-60" : "opacity-30 md:opacity-0 md:group-hover:opacity-100"} focus:opacity-100 transition-opacity text-muted-foreground hover:text-accent shrink-0 ${className || ""}`}
      >
        <FileSearch className="h-3.5 w-3.5" />
      </button>
      {open && <ProvenancePanel pkey={pkey} pp={pp} year={year} onClose={() => setOpen(false)} />}
    </>
  );
}
